import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Upload, Video, Music, FileText, Image, File } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import FileUploader from '../../components/FileUploader';

const FILE_TYPE_ICONS = {
  image: Image,
  mp4: Video,
  mp3: Music,
  pdf: FileText,
  doc: File,
  file: File
};

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    thumbnail: '',
    video_url: '',
    image_url: '',
    modules: [],
    files: []
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null
      };

      if (editingCourse) {
        await api.put(`/courses/${editingCourse.id}`, data);
        toast.success('Course updated successfully');
      } else {
        await api.post('/courses', data);
        toast.success('Course created successfully');
      }

      setShowModal(false);
      setEditingCourse(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        original_price: '',
        thumbnail: '',
        video_url: '',
        image_url: '',
        modules: [],
        files: []
      });
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      description: course.description,
      price: course.price.toString(),
      original_price: course.original_price?.toString() || '',
      thumbnail: course.thumbnail || '',
      video_url: course.video_url || '',
      image_url: course.image_url || '',
      modules: course.modules || [],
      files: course.files || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await api.delete(`/courses/${id}`);
      toast.success('Course deleted');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-courses-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold" data-testid="courses-title">Manage Courses</h1>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-course-button"
          >
            <Plus className="w-5 h-5" />
            Add Course
          </button>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div key={course.id} className="glass-card p-6" data-testid={`course-card-${course.id}`}>
              {course.thumbnail && (
                <div className="aspect-video rounded-lg overflow-hidden mb-4">
                  <img src={course.thumbnail} alt={course.name} className="w-full h-full object-cover" />
                </div>
              )}
              <h3 className="text-xl font-semibold mb-2">{course.name}</h3>
              <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{course.description}</p>
              <p className="text-2xl font-bold text-primary mb-4">₹{course.price}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(course)}
                  className="flex-1 px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-all"
                  data-testid={`edit-course-${course.id}`}
                >
                  <Edit className="w-4 h-4 inline mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(course.id)}
                  className="px-4 py-2 bg-destructive/20 text-destructive rounded-lg hover:bg-destructive/30 transition-all"
                  data-testid={`delete-course-${course.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="course-modal">
            <div className="glass-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">
                {editingCourse ? 'Edit Course' : 'Add New Course'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Course Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    data-testid="course-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    data-testid="course-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Offer Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="course-price-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Regular Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.original_price}
                      onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="course-original-price-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Thumbnail URL</label>
                  <input
                    type="url"
                    value={formData.thumbnail}
                    onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    placeholder="https://example.com/image.jpg"
                    data-testid="course-thumbnail-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Video URL (YouTube/MP4)
                  </label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
                    data-testid="course-video-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Header Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    placeholder="https://example.com/header.jpg"
                    data-testid="course-image-input"
                  />
                </div>

                {/* Course Files - Device Upload */}
                <div className="border border-primary/30 rounded-xl p-4 bg-primary/5">
                  <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-primary" />
                    Course Files (Upload from Device)
                  </label>
                  <FileUploader
                    contentType="course"
                    existingFiles={formData.files || []}
                    onFileUploaded={(file) => {
                      setFormData({
                        ...formData,
                        files: [...(formData.files || []), file]
                      });
                    }}
                    onFileRemoved={(idx) => {
                      setFormData({
                        ...formData,
                        files: (formData.files || []).filter((_, i) => i !== idx)
                      });
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Upload course materials: JPEG, GIF, PNG images, MP4 videos, MP3 audio, PDF documents
                  </p>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                    data-testid="save-course-button"
                  >
                    {editingCourse ? 'Update' : 'Create'} Course
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCourse(null);
                      setFormData({
                        name: '',
                        description: '',
                        price: '',
                        original_price: '',
                        thumbnail: '',
                        modules: [],
                        files: []
                      });
                    }}
                    className="px-8 py-4 bg-white/5 text-foreground border border-white/10 hover:bg-white/10 rounded-full font-semibold"
                    data-testid="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCourses;
